import React, { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';
import {
  BasicTrackingFn,
  ExternalTrackingFn,
  ExternalGetTrackingTagsFn,
  InternalTrackingFn,
  PageHeading,
  Params,
  InternalGetTrackingTagsFn,
  BasicGetTrackingTagsFn,
  Context,
} from '../types';
import { useCorePageState } from '../page/provider';
import { ContextManager } from '../context/manager';
import cache from '../cache/cache';

type AnalyticsContextType = {
  trackPageEvent: BasicTrackingFn;
  getAnalyticTrackingTags: BasicGetTrackingTagsFn;
};

const analyticsContext = createContext<AnalyticsContextType>(null);

type AnalyticsProviderProps = {
  children: ReactNode;
  externalTrackingFn?: ExternalTrackingFn;
  externalGetTrackingTags?: ExternalGetTrackingTagsFn;
};

const getTitleFromHeading = (heading: PageHeading): string => {
  if (typeof heading.title === 'string') {
    return heading.title;
  }
  if (heading.title.length === 0) {
    return '';
  }
  if (typeof heading.title[0] === 'string') {
    return heading.title[0];
  }
  return heading.title[0].displayText || '';
};

const getPageContext = (context?: Context): Params => {
  const activeContext = context || ContextManager.getInstance().get();
  const pageContext: Params = activeContext
    ? Object.keys(activeContext.settings.values).reduce((acc, key) => {
        const values = activeContext.settings.values[key];
        return {
          ...acc,
          [key]: values.map(entity => entity.value),
        };
      }, {})
    : {};
  return pageContext;
};

function AnalyticsProvider({ children, externalTrackingFn, externalGetTrackingTags }: AnalyticsProviderProps) {
  const { url, dsl, dslLoading, contextLoading, error } = useCorePageState();

  const readyForTracking = useMemo(
    () => dslLoading === false && contextLoading === false && !error,
    [dslLoading, contextLoading, error]
  );

  /**
   * Sends tracking events to the external tracking function.
   * Page context and other information is automatically included
   */
  const doTrack: InternalTrackingFn = useCallback(
    (eventName, meta, options) => {
      if (!readyForTracking) {
        return;
      }

      const pageTitle = dsl?.heading ? getTitleFromHeading(dsl.heading) : '';
      const pageContext = getPageContext(options.context);

      if (externalTrackingFn) {
        return externalTrackingFn(eventName, { pageUrl: url, pageTitle, pageContext, ...meta }, options);
      }
    },
    [readyForTracking, externalTrackingFn, url, dsl?.heading]
  );

  const doGetTrackingTags: InternalGetTrackingTagsFn = useCallback(
    (eventName, meta) => {
      const pageTitle = dsl?.heading ? getTitleFromHeading(dsl.heading) : '';
      const pageContext = getPageContext();
      if (externalGetTrackingTags) {
        return externalGetTrackingTags(eventName, {
          pageUrl: url,
          pageTitle,
          pageContext,
          ...meta,
        });
      }
    },
    [externalGetTrackingTags, url, dsl?.heading]
  );

  const trackPageEvent: BasicTrackingFn = useCallback(
    (eventName, eventParams = {}, options = {}) => {
      return doTrack(eventName, { eventParams }, options);
    },
    [doTrack]
  );

  const getAnalyticTrackingTags: BasicGetTrackingTagsFn = useCallback(
    (eventName, eventParams = {}) => {
      return doGetTrackingTags(eventName, { eventParams });
    },
    [doGetTrackingTags]
  );

  if (trackPageEvent && readyForTracking) cache('trackPageEvent', trackPageEvent);

  const currentContext: AnalyticsContextType = useMemo(() => {
    return {
      trackPageEvent,
      getAnalyticTrackingTags,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackPageEvent, doTrack, getAnalyticTrackingTags]);

  return <analyticsContext.Provider value={currentContext}>{children}</analyticsContext.Provider>;
}

function useAnalyticsContext() {
  const context = useContext(analyticsContext);
  if (!context) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
}

export { AnalyticsProvider, useAnalyticsContext };
